import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
  Paper, Link, Avatar, Box, Typography, Card, CardContent, Stack, Chip,
  TablePagination,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import theme from '../theme';

export const calculateAge = (bdate) => {
  if (!bdate) return 'Не указан';
  const parts = bdate.split('.');
  if (parts.length < 3) return 'Неполная дата';

  const birthDate = new Date(+parts[2], parts[1] - 1, +parts[0]);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

export const getStatusLabel = (statusValue) => {
  const statuses = {
    1: 'Не женат/не замужем',
    2: 'Есть друг/подруга',
    3: 'Помолвлен/помолвлена',
    4: 'Женат/замужем',
    5: 'Всё сложно',
    6: 'В активном поиске',
    7: 'Влюблён/влюблена',
    8: 'В гражданском браке',
  };
  return statuses[statusValue] || 'Не указан';
};

const formatLastSeen = (lastSeen) => {
  if (!lastSeen?.time) return '—';
  return new Date(lastSeen.time * 1000).toLocaleDateString('ru-RU');
};

const formatOccupation = (occupation) => {
  if (!occupation) return '—';
  return occupation.name || '—';
};

const formatEducation = (user) => {
  if (user.university_name) {
    return user.faculty_name
      ? `${user.university_name}, ${user.faculty_name}`
      : user.university_name;
  }
  return '—';
};

function MobileCardList({ audience }) {
  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {audience.map((user, index) => {
        const age = calculateAge(user.bdate);
        const sex = user.sex === 1 ? 'Ж' : user.sex === 2 ? 'М' : null;
        const city = user.city?.title;
        const isOnline = user.online === 1;
        return (
          <Card
            key={user.id}
            sx={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Avatar
                  src={user.photo_100}
                  alt={user.first_name}
                  sx={{
                    width: 44,
                    height: 44,
                    fontSize: 14,
                    background: 'linear-gradient(135deg, #6C63FF, #00D4AA)',
                    flexShrink: 0,
                  }}
                >
                  {user.first_name?.[0]}
                </Avatar>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      #{index + 1}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {sex && (
                      <Chip label={sex} size="small" sx={{ height: 20, fontSize: 11 }} />
                    )}
                    {typeof age === 'number' && (
                      <Chip label={`${age} лет`} size="small" sx={{ height: 20, fontSize: 11 }} />
                    )}
                    {city && (
                      <Chip label={city} size="small" sx={{ height: 20, fontSize: 11 }} />
                    )}
                    {isOnline && (
                      <Chip
                        label="онлайн"
                        size="small"
                        sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(0,212,170,0.18)', color: '#00D4AA' }}
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {getStatusLabel(user.relation)}
                  </Typography>

                  {(user.university_name || user.occupation?.name) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.25,
                      }}
                    >
                      {formatEducation(user) !== '—' ? formatEducation(user) : formatOccupation(user.occupation)}
                    </Typography>
                  )}

                  <Link
                    href={`https://vk.com/${user.domain || `id${user.id}`}`}
                    target="_blank"
                    rel="noopener"
                    sx={{ color: '#6C63FF', fontSize: 12, mt: 0.5, display: 'inline-block' }}
                  >
                    Открыть профиль →
                  </Link>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

function AudienceTable({ audience, displayTotal, selectedFields: sf }) {
  const sf_ = sf || {};
  const show = (k) => sf_[k] !== false;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => { setPage(0); }, [audience.length]);

  if (!audience || audience.length === 0) return null;

  const displayed = audience.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const total = displayTotal || audience.length;

  const pager = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
      rowsPerPageOptions={[25, 50, 100]}
      labelRowsPerPage="Строк:"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count.toLocaleString("ru-RU")}`}
      sx={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
    />
  );

  if (isMobile) {
    return (
      <>
        <MobileCardList audience={displayed} />
        {audience.length > rowsPerPage && pager}
      </>
    );
  }

  return (
    <>
    <TableContainer
      component={Paper}
      sx={{
        mt: 4,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', width: 40 }}>#</TableCell>
            {show('first_name') && <TableCell>Имя</TableCell>}
            {show('last_name') && <TableCell>Фамилия</TableCell>}
            {show('sex') && <TableCell>Пол</TableCell>}
            {show('age') && <TableCell>Возраст</TableCell>}
            {show('relation') && <TableCell>Семейное положение</TableCell>}
            {show('city') && <TableCell>Город</TableCell>}
            {show('country') && <TableCell>Страна</TableCell>}
            {show('education') && <TableCell>Образование</TableCell>}
            {show('occupation') && <TableCell>Работа/Учёба</TableCell>}
            {show('online') && <TableCell>Онлайн</TableCell>}
            {show('link') && <TableCell>Профиль</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayed.map((user, index) => (
            <TableRow
              key={user.id}
              sx={{
                '&:hover': {
                  background: 'rgba(108,99,255,0.06)',
                },
                transition: 'background 0.15s',
              }}
            >
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {page * rowsPerPage + index + 1}
              </TableCell>
              {show('first_name') && <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {show('photo') && <Avatar
                    src={user.photo_100}
                    alt={user.first_name}
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.75rem',
                      background: 'linear-gradient(135deg, #6C63FF, #00D4AA)',
                    }}
                  >
                    {user.first_name?.[0]}
                  </Avatar>}
                  <Typography variant="body2">{user.first_name}</Typography>
                </Box>
              </TableCell>}
              {show('last_name') && <TableCell>{user.last_name}</TableCell>}
              {show('sex') && <TableCell>{user.sex === 1 ? 'Ж' : user.sex === 2 ? 'М' : '—'}</TableCell>}
              {show('age') && <TableCell>{calculateAge(user.bdate)}</TableCell>}
              {show('relation') && <TableCell>{getStatusLabel(user.relation)}</TableCell>}
              {show('city') && <TableCell>{user.city?.title || '—'}</TableCell>}
              {show('country') && <TableCell>{user.country?.title || '—'}</TableCell>}
              {show('education') && <TableCell>{formatEducation(user)}</TableCell>}
              {show('occupation') && <TableCell>{formatOccupation(user.occupation)}</TableCell>}
              {show('online') && <TableCell>{user.online === 1 ? '✓' : formatLastSeen(user.last_seen)}</TableCell>}
              {show('link') && <TableCell>
                <Link
                  href={`https://vk.com/${user.domain || `id${user.id}`}`}
                  target="_blank"
                  rel="noopener"
                  sx={{ color: '#6C63FF', '&:hover': { color: '#8B84FF' } }}
                >
                  Перейти
                </Link>
              </TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    {audience.length > rowsPerPage && pager}
    </>
  );
}

export default AudienceTable;
